import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { Loading, Spinner } from '@nextui-org/react';
import { mstore } from "../../store";
import checkAccess from '../../lib/checkAccess';
import { fetchWithJson } from "../../lib/api";
import endpoint from "../../config/endpoint.json";
import { error } from "../../lib/userAlerts";

// Import components
import ManageArtifacts from "../DriverConnect/ManageArtifacts";
import ShipmentCustomFields from "../DriverConnect/ShipmentCustomFields";
import GPSInterval from "../DriverConnect/GPSInterval";
import SafetyDocumentType from "../DriverConnect/safetyDocumentType";
import AutoClose from "../DriverConnect/AutoClose";
import ReceiptArchiveDays from "../DriverConnect/ReceiptArchiveDays";
import NewsFeed from "../DriverConnect/NewsFeedSettings";
import CannedMessageSettings from "../DriverConnect/CannedMessageSettings";
import AutoLinkSettings from "../DriverConnect/AutoLinkSettings";
import DetentionManagement from "../DriverConnect/DetentionManagement";

// Tab styles with improved visual feedback
const tabStyles = {
  tabList: 'flex border-b border-gray-200 mb-6 overflow-x-auto',
  tab: 'px-4 py-3 font-medium text-gray-600 cursor-pointer border-b-2 border-transparent hover:text-primary hover:border-primary transition-colors duration-200 whitespace-nowrap',
  selectedTab: 'text-primary border-primary font-semibold',
  tabPanel: 'mt-6'
};

const Settings = () => {
  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  const access = useSelector(state => state.navigation.Access);

  const [state, setState] = useState({
    activeTabId: 0,
    fetching: true,
    featureList: [],
    rawfeatureList: []
  });

  const sortFeaturesList = useCallback(() => {
    const filteredList = state.rawfeatureList.filter(item => {
      const featureMap = {
        0: [1003],
        1: [1004, 1007],
        2: [1005],
        3: [1008, 1009],
        4: [1010],
        5: [1012],
        6: [1015],
        7: [1019]
      };
      return featureMap[state.activeTabId]?.includes(item.feature_id);
    });

    setState(prev => ({ ...prev, featureList: filteredList, fetching: false }));
  }, [state.activeTabId, state.rawfeatureList]);

  const fetchFeatureList = useCallback(async () => {
    try {
      const response = await fetchWithJson(endpoint.settingsProductFeatures, {
        company_id: clientId,
      });
      const data = await response.json();
      setState(prev => ({ ...prev, rawfeatureList: data }));
    } catch (err) {
      error("Oops! error occurred during fetch feature list");
      console.error(err);
    }
  }, [clientId]);

  useEffect(() => {
    checkAccess(clientId);
    fetchFeatureList();
  }, [clientId, fetchFeatureList]);

  useEffect(() => {
    if (state.rawfeatureList.length > 0) {
      sortFeaturesList();
    }
  }, [state.activeTabId, state.rawfeatureList, sortFeaturesList]);

  const handleTabChange = (activeTabId) => {
    setState(prev => ({ ...prev, activeTabId }));
  };

  if (state.fetching) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Spinner size="lg" color="primary" />
        <p className="mt-4 text-gray-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="pa2 ml2">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your application settings and preferences</p>
      </div>

      <Tabs 
        selectedIndex={state.activeTabId} 
        onSelect={handleTabChange}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <TabList className={tabStyles.tabList}>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              General
            </div>
          </Tab>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              Documents
            </div>
          </Tab>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              Receipts
            </div>
          </Tab>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              Location
            </div>
          </Tab>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              Safety
            </div>
          </Tab>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              News Feed
            </div>
          </Tab>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              Canned Messages
            </div>
          </Tab>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              Detention
            </div>
          </Tab>
          <Tab className={tabStyles.tab} selectedClassName={tabStyles.selectedTab}>
            <div className="flex items-center">
              Autolink
            </div>
          </Tab>
        </TabList>

        <TabPanel className={tabStyles.tabPanel}>
          <div className="space-y-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">Auto Close Settings</h2>
              <AutoClose
                fetching={state.fetching}
                featureList={state.featureList}
              />
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">Custom Fields</h2>
              <ShipmentCustomFields />
            </div>
          </div>
        </TabPanel>

        <TabPanel className={tabStyles.tabPanel}>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Document Management</h2>
            <ManageArtifacts
              fetching={state.fetching}
              featureList={state.featureList}
            />
          </div>
        </TabPanel>

        <TabPanel className={tabStyles.tabPanel}>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Receipt Archive Days</h2>
            <ReceiptArchiveDays
              fetching={state.fetching}
              featureList={state.featureList}
            />
          </div>
        </TabPanel>

        <TabPanel className={tabStyles.tabPanel}>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">GPS Interval</h2>
            <GPSInterval
              fetching={state.fetching}
              featureList={state.featureList}
            />
          </div>
        </TabPanel>
        
        <TabPanel className={tabStyles.tabPanel}>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Safety Document Type</h2>
            <SafetyDocumentType
              fetching={state.fetching}
              featureList={state.featureList}
            />
          </div>
        </TabPanel>

        <TabPanel className={tabStyles.tabPanel}>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">News Feed</h2>
            <NewsFeed
              fetching={state.fetching}
              featureList={state.featureList}
            />
          </div>
        </TabPanel>

        <TabPanel className={tabStyles.tabPanel}>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Canned Messages</h2>
            <CannedMessageSettings
              fetching={state.fetching}
              featureList={state.featureList}
            />
          </div>
        </TabPanel>

        <TabPanel className={tabStyles.tabPanel}>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Detention</h2>
            <DetentionManagement
              fetching={state.fetching}
              featureList={state.featureList}
            />
          </div>
        </TabPanel>
        
        <TabPanel className={tabStyles.tabPanel}>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Autolink</h2>
            <AutoLinkSettings/>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default Settings;